'use client';
import type { ComponentProps } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import LucideIcon from '@/components/ui/LucideIcon';
import { Stack } from '@chakra-ui/react';
import { getWishlistItem } from '@/utils/domain/wishlist';
import { useWishlistToggle } from '@/hooks/wishlist/useWishlistToggle';
import { debounce } from 'es-toolkit';

type ProductCardWishListButtonProps = {
  sku: string;
} & ComponentProps<typeof Stack>;

export const ProductCardWishListButton = ({ sku, ...rest }: ProductCardWishListButtonProps) => {
  const { wishlistData, toggleWishlist } = useWishlistToggle();
  const { isWished: isServerWished } = getWishlistItem(wishlistData?.skus ?? [], sku);
  const [isWished, setIsWished] = useState(isServerWished);

  const toggleWishlistRef = useRef(toggleWishlist);
  const skuRef = useRef(sku);

  useEffect(() => {
    toggleWishlistRef.current = toggleWishlist;
    skuRef.current = sku;
  }, [toggleWishlist, sku]);

  const debouncedToggleRef = useRef(
    debounce(() => {
      toggleWishlistRef.current(sku);
    }, 1000),
  );

  useEffect(() => {
    setIsWished(isServerWished);
  }, [isServerWished]);

  useEffect(() => {
    const debouncedFn = debouncedToggleRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const handleWishlistToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsWished((prev) => !prev);
    debouncedToggleRef.current();
  };

  return (
    <Stack
      as="button"
      cursor="pointer"
      zIndex={1}
      backgroundColor="common.100"
      borderRadius="20px"
      border="1px solid"
      height="fit-content"
      borderColor="transparent"
      padding={2}
      transition="border-color 0.2s ease-in-out"
      onClick={(e) => {
        e.stopPropagation();
        handleWishlistToggle(e);
      }}
      _hover={{
        '& > svg': {
          stroke: 'brand.40',
        },
        borderColor: 'brand.5',
      }}
      aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
      {...rest}
    >
      <LucideIcon
        name="Heart"
        color="neutral.200"
        stroke={isWished ? 'brand.40' : 'neutral.200'}
        fill={isWished ? 'brand.40' : 'none'}
        width={4}
        height={4}
        pointerEvents="none"
        transition="color 0.2s ease-in-out, stroke 0.2s ease-in-out, fill 0.2s ease-in-out"
      />
    </Stack>
  );
};
